import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Button, Card, Chip, CircularProgress, InputAdornment, IconButton,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PhoneIcon from '@mui/icons-material/Phone';
import SyncIcon from '@mui/icons-material/Sync';
import { useQuery, useMutation } from '@apollo/client';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import { useAuth } from '../../hooks/useAuth';
import CallLogDialog, { CallLogEntry } from './CallLogDialog';
import { fmtEpoch, fmtDays, fmtAgo, severityColor, severityLabel } from './format';
import RechargeTypeTag, { rechargeTypeLabel } from '../../components/RechargeTypeTag';
import {
  GET_EXPIRING_CERTIFICATES,
  GET_EXPIRING_SAARTHI_PRO,
  GET_CERT_EXPIRY_STATS,
  GET_CERT_REFRESH_STATUS,
  LOG_EXPIRY_CALL,
  REFRESH_CERTIFICATE_DATA,
  ExpiryKind,
  ExpiryVehicle,
  ExpiringCertificatesData,
  ExpiringSaarthiProData,
  CertExpiryStatsData,
  CertRefreshStatusData,
  RefreshCertificateDataResult,
} from './queries';

/**
 * Certificate / Saarthi Pro expiry tracker — the oem-tool port of the partner
 * portal's cert-expiry console (partner-portal-frontend pages/CertExpiry.tsx).
 *
 * Two views over the same row shape: AIS-140 certificates (issued + 2 years)
 * and Saarthi Pro SIM subscriptions. Unlike the partner portal, which pulls the
 * whole fleet out of Redis and filters it in the browser, every filter here is a
 * query variable — the server pages, searches and buckets by severity, so the
 * counts in the badges and the rows in the table are computed the same way and
 * cannot drift.
 */

type SeverityFilter = 'all' | 'critical' | 'warning' | 'expired';

const COLUMNS = [
  'Sr.No', 'Customer', 'Phone', 'VIN', 'IMEI', 'Vehicle',
  'AIS-140 Cert Expiry', 'Saarthi Pro Expiry', 'Days Left', 'Status',
  'Recharge Type', 'Dealer', 'Last Contact', 'Last Reason', 'Action',
];

const cellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: { xs: '4px', sm: '6px' },
  fontSize: { xs: '0.75rem', sm: '0.85rem' },
  borderRight: '1px solid #e0e0e0',
};

const subTabStyle = (isActive: boolean) => ({
  fontWeight: 600,
  fontSize: '1rem',
  px: 3,
  py: 1.5,
  borderRadius: '20px',
  textTransform: 'none' as const,
  cursor: 'pointer',
  transition: 'all 0.25s ease',
  color: isActive ? '#fff' : '#555',
  backgroundColor: isActive ? '#E60012' : 'transparent',
  '&:hover': { backgroundColor: isActive ? '#E60012' : '#ffffffc0' },
  minWidth: 'auto',
});

/**
 * How often to ask the server whether the refresh has finished. A sweep runs
 * 3-7 minutes, so this is ~40 polls at the long end — cheap (the query reads an
 * in-memory object) and slow enough not to look like a retry storm in the logs.
 */
const REFRESH_POLL_MS = 10000;

const CertificateExpiry = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { canCreate, canUpdate } = useAuth();

  const [kind, setKind] = useState<ExpiryKind>('ais');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [windowDays, setWindowDays] = useState(60);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selected, setSelected] = useState<ExpiryVehicle | null>(null);
  const [callBusy, setCallBusy] = useState(false);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Any change to what is being asked for starts again at page 1 — otherwise a
  // filter that narrows the list to 3 rows leaves you looking at page 7 of it.
  useEffect(() => {
    setPage(0);
  }, [kind, severity, windowDays, debouncedSearch]);

  const variables = {
    offset: page * rowsPerPage,
    limit: rowsPerPage,
    search: debouncedSearch || undefined,
    severity: severity === 'all' ? undefined : severity,
    windowDays,
    // "Expired" is its own bucket rather than part of the window, matching the
    // partner portal's default of dropping already-lapsed vehicles.
    includeExpired: severity === 'expired',
  };

  // Both documents are always mounted but only the active one is allowed to
  // fetch: `skip` keeps the idle view from issuing a second full query on every
  // keystroke, while still letting Apollo hold its result for an instant switch.
  const certQuery = useQuery<ExpiringCertificatesData>(GET_EXPIRING_CERTIFICATES, {
    variables,
    skip: kind !== 'ais',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const proQuery = useQuery<ExpiringSaarthiProData>(GET_EXPIRING_SAARTHI_PRO, {
    variables,
    skip: kind !== 'saarthi_pro',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { data: statsData, refetch: refetchStats } = useQuery<CertExpiryStatsData>(
    GET_CERT_EXPIRY_STATS,
    { variables: { kind, windowDays }, fetchPolicy: 'network-only' },
  );
  const stats = statsData?.certificateExpiryStats;

  const active = kind === 'ais' ? certQuery : proQuery;
  const payload = kind === 'ais'
    ? certQuery.data?.expiringCertificates
    : proQuery.data?.expiringSaarthiPro;

  const rows: ExpiryVehicle[] = useMemo(() => payload?.data ?? [], [payload]);
  const totalCount = payload?.count ?? 0;

  /**
   * Refresh state of the legacy certificate-date pull — manual or cron, so this
   * also answers "how old is what I am looking at". Polling is driven by the
   * effect below rather than a static pollInterval: idle is the normal state and
   * there is nothing to watch until a sweep is actually running.
   */
  const {
    data: refreshData,
    startPolling: startRefreshPolling,
    stopPolling: stopRefreshPolling,
  } = useQuery<CertRefreshStatusData>(GET_CERT_REFRESH_STATUS, {
    fetchPolicy: 'network-only',
  });
  const refresh = refreshData?.certificateRefreshStatus;
  const refreshRunning = refresh?.status === 'running';

  const [refreshCertificateData, { loading: refreshStarting }] =
    useMutation<RefreshCertificateDataResult>(REFRESH_CERTIFICATE_DATA, {
      // The mutation and the status query return the same object, but Apollo
      // cannot know that (no id, different root fields), so the query would go
      // on serving "idle" until the first poll ten seconds later — the button
      // would sit there looking un-clicked. Seed it with what the mutation
      // already told us.
      update: (cache, { data }) => {
        if (!data?.refreshCertificateData) return;
        cache.writeQuery({
          query: GET_CERT_REFRESH_STATUS,
          data: { certificateRefreshStatus: data.refreshCertificateData },
        });
      },
    });

  // Whether a run was in flight the last time we looked. Reporting the outcome
  // is driven off the running -> finished TRANSITION, not off the status alone,
  // because the status also describes the last CRON run: without this, opening
  // the page would announce "refresh complete" for a sweep that finished at
  // 03:40 and that nobody asked for. It also means a refresh survives a page
  // reload — we pick the running state back up and still report it.
  const wasRefreshing = useRef(false);

  useEffect(() => {
    if (refreshRunning) {
      wasRefreshing.current = true;
      startRefreshPolling(REFRESH_POLL_MS);
      return;
    }
    stopRefreshPolling();
    if (!wasRefreshing.current) return;
    wasRefreshing.current = false;

    if (refresh?.status === 'failed') {
      enqueueSnackbar(refresh.error || 'Certificate refresh failed.', {
        variant: 'error',
      });
      return;
    }
    if (refresh?.status === 'done') {
      enqueueSnackbar(
        `Certificate data refreshed — ${refresh.written ?? 0} updated, ` +
          `${refresh.renewed ?? 0} re-certified.`,
        { variant: 'success' },
      );
      // The pull rewrites the issue dates the whole list is derived from, so
      // both the rows and the badge counts have to come back.
      active.refetch();
      refetchStats();
    }
    // finishedAt is in here as well as status: a run that aborts on the
    // advisory lock lands on "failed" when the previous run had also failed,
    // and keying on status alone would see no change — the effect would never
    // fire, so polling would never stop. Every run has its own finishedAt.
    //
    // `active` and refetchStats are deliberately NOT deps: they change identity
    // on every filter change, which would re-run this and re-announce a refresh
    // that finished long ago.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshRunning, refresh?.status, refresh?.finishedAt]);

  const handleRefresh = async () => {
    try {
      const res = await refreshCertificateData();
      const state = res.data?.refreshCertificateData;
      if (state?.status === 'failed') {
        enqueueSnackbar(state.error || 'Certificate refresh failed to start.', {
          variant: 'error',
        });
        return;
      }
      wasRefreshing.current = true;
      startRefreshPolling(REFRESH_POLL_MS);
      enqueueSnackbar(
        'Refreshing certificate dates from the OEM tool — this takes a few minutes. You can keep working.',
        { variant: 'info' },
      );
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not start the refresh.', {
        variant: 'error',
      });
    }
  };

  const [logCall] = useMutation(LOG_EXPIRY_CALL);

  const handleLogCall = async ({ note, reason, attendedBy }: CallLogEntry) => {
    if (!selected || !note.trim()) {
      enqueueSnackbar('Write a call note before logging.', { variant: 'warning' });
      return;
    }
    setCallBusy(true);
    try {
      const res = await logCall({
        variables: {
          vin: selected.vin,
          imei: selected.imei,
          callType: kind,
          callNote: note.trim(),
          // Both optional. Sending undefined rather than '' matters: the
          // backend only overwrites a stored value when the new one is
          // non-empty, so a blank field on a follow-up call preserves what the
          // previous call recorded instead of clearing it.
          callerResponse: reason || undefined,
          attendedBy: attendedBy.trim() || undefined,
        },
      });
      const r = res.data?.logCertificateExpiryCall;
      if (r?.success) {
        enqueueSnackbar(r.message || 'Call logged.', { variant: 'success' });
        setSelected(null);
        // The row carries its own last-contact column and the badge counts
        // "contacted today", so both have to come back for the write to show.
        active.refetch();
        refetchStats();
      } else {
        enqueueSnackbar(r?.error || 'Failed to log the call.', { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to log the call.', { variant: 'error' });
    } finally {
      setCallBusy(false);
    }
  };

  /**
   * Exports the page you are looking at, not the whole result set — the query is
   * paged server-side, and silently exporting 20 of 3,000 rows as if it were
   * everything is worse than exporting the page you can see.
   */
  const handleExport = () => {
    if (rows.length === 0) {
      enqueueSnackbar('Nothing to export.', { variant: 'warning' });
      return;
    }
    const sheet = XLSX.utils.json_to_sheet(
      rows.map((r, i) => ({
        'Sr.No': page * rowsPerPage + i + 1,
        Customer: r.customerName || '',
        Phone: r.customerMobile || '',
        VIN: r.vin || '',
        IMEI: r.imei || '',
        ICCID: r.iccid || '',
        Vehicle: r.vehicleModel || '',
        'AIS-140 Cert Expiry': fmtEpoch(r.claExpiresOn),
        'Saarthi Pro Expiry': fmtEpoch(r.saarthiProExpiryDate),
        'Days Left': r.daysToExpiry ?? '',
        Status: severityLabel(r.severity),
        'Recharge Type': rechargeTypeLabel(r.rechargeType),
        Dealer: r.dealerName || '',
        'Dealer Contact': r.dealerContactNumber || '',
        RTO: r.rtoName || '',
        State: r.registrationState || '',
        'Last Contact': fmtEpoch(r.lastCallAt),
        'Logged By': r.lastCallBy || '',
        'Attended By': r.lastCallAttendedBy || '',
        'Last Reason': r.lastCallReason || '',
      })),
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Expiry');
    XLSX.writeFile(
      book,
      `${kind === 'ais' ? 'certificate' : 'saarthi-pro'}-expiry-page-${page + 1}.xlsx`,
    );
  };

  const badges = [
    { label: `Expiring < 30 days`, value: stats?.criticalCount, color: '#d32f2f' },
    { label: `Expiring 30–60 days`, value: stats?.warningCount, color: '#ed6c02' },
    { label: 'Already expired', value: stats?.expiredCount, color: '#7b1fa2' },
    { label: 'Contacted today', value: stats?.contactedTodayCount, color: '#2e7d32' },
  ];

  const canRefresh = canUpdate('CERTIFICATE-EXPIRY');
  const refreshBusy = refreshStarting || refreshRunning;

  // Says what the button will do, and — the part that actually matters to
  // someone deciding whether to press it — how old the data already is.
  const refreshTooltip = !canRefresh
    ? 'You need update access on CERTIFICATE-EXPIRY to refresh.'
    : refreshRunning
      ? `Refreshing since ${fmtAgo(refresh?.startedAt)}${
          refresh?.triggeredBy ? ` (started by ${refresh.triggeredBy})` : ''
        } — a full pull takes a few minutes.`
      : [
          'Pull the latest AIS-140 certificate dates from the OEM tool now.',
          `Last refreshed ${fmtAgo(refresh?.finishedAt)}${
            refresh?.triggeredBy === 'cron'
              ? ' (automatic)'
              : refresh?.triggeredBy
                ? ` by ${refresh.triggeredBy}`
                : ''
          }.`,
          'Runs automatically every 3 hours.',
        ].join(' ');

  const severityFilters: Array<{ key: SeverityFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'critical', label: 'Critical (<30d)' },
    { key: 'warning', label: 'Warning (30–60d)' },
    { key: 'expired', label: 'Expired' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box
          sx={{
            display: 'inline-flex', alignItems: 'center', backgroundColor: '#f0f0f0',
            borderRadius: '24px', padding: '4px', gap: '4px',
          }}
        >
          <Button disableRipple onClick={() => setKind('ais')} sx={subTabStyle(kind === 'ais')}>
            Certificate Expiry
          </Button>
          <Button disableRipple onClick={() => setKind('saarthi_pro')} sx={subTabStyle(kind === 'saarthi_pro')}>
            Saarthi Pro Expiry
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {badges.map((b) => (
            <Box
              key={b.label}
              sx={{
                minWidth: 116, px: 2, py: 1, borderRadius: 2, backgroundColor: '#fff',
                border: '1px solid #e0e0e0', borderTop: `3px solid ${b.color}`,
                boxShadow: '0 2px 6px -4px #b0b0b0', textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: b.color, lineHeight: 1 }}>
                {b.value ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: '#666', mt: 0.5 }}>{b.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Card
        sx={{
          width: '100%', maxWidth: '100vw', mx: 'auto', boxShadow: 2, borderRadius: 3, p: 0,
          display: 'flex', flexDirection: 'column',
          height: { xs: 'auto', md: 'calc(93vh - 190px)' }, bgcolor: '#fff', minHeight: 100,
        }}
      >
        <Box
          sx={{
            position: 'sticky', top: 0, zIndex: 20, background: '#fff',
            borderTopLeftRadius: 8, borderTopRightRadius: 8,
            boxShadow: '0 2px 8px -4px #e0e0e0', px: { xs: 1, sm: 2 }, pt: 2, pb: 2,
          }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: 'column', md: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, gap: 2 }}
          >
            <Box display="flex" gap={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              {severityFilters.map((f) => (
                <Chip
                  key={f.key}
                  label={f.label}
                  onClick={() => setSeverity(f.key)}
                  variant={severity === f.key ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.78rem',
                    backgroundColor: severity === f.key ? '#E60012' : 'transparent',
                    color: severity === f.key ? '#fff' : '#555',
                    borderColor: '#ddd',
                    '&:hover': { backgroundColor: severity === f.key ? '#c1000f' : '#f5f5f5' },
                  }}
                />
              ))}
            </Box>

            <Box display="flex" gap={1} alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap' }}>
              <TextField
                select
                size="small"
                label="Window"
                value={windowDays}
                onChange={(e) => setWindowDays(Number(e.target.value))}
                // The window is disabled for the Expired bucket: those rows are
                // already past their date, so "next N days" says nothing about them.
                disabled={severity === 'expired'}
                sx={{ minWidth: 130, background: '#f5f5f5' }}
              >
                <MenuItem value={30}>Next 30 days</MenuItem>
                <MenuItem value={60}>Next 60 days</MenuItem>
                <MenuItem value={90}>Next 90 days</MenuItem>
                <MenuItem value={180}>Next 180 days</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder="VIN / IMEI / ICCID / customer"
                variant="outlined"
                sx={{ minWidth: 240, background: '#f5f5f5' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {/*
                Certificate tab only. The pull it triggers fetches AIS-140
                certificate dates from the legacy OEM tool and nothing else --
                Saarthi Pro dates come from Sensorise on their own cron, so a
                Refresh button on that tab would promise something it does not do.
              */}
              {kind === 'ais' && (
                <Tooltip title={refreshTooltip} placement="top" arrow>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleRefresh}
                      disabled={refreshBusy || !canRefresh}
                      startIcon={
                        refreshBusy
                          ? <CircularProgress size={14} sx={{ color: '#E60012' }} />
                          : <SyncIcon sx={{ fontSize: 16 }} />
                      }
                      sx={{
                        textTransform: 'none', borderRadius: '20px', height: 36,
                        fontSize: '0.8rem', whiteSpace: 'nowrap',
                        color: '#E60012', borderColor: '#E60012',
                        '&:hover': { borderColor: '#c1000f', backgroundColor: '#E600120A' },
                      }}
                    >
                      {refreshBusy ? 'Refreshing…' : 'Refresh data'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Export this page to Excel" placement="top" arrow>
                <IconButton sx={{ color: '#000' }} onClick={handleExport}>
                  <CloudDownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, py: 2,
            overflow: 'auto', display: 'flex', flexDirection: 'column',
          }}
        >
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{ boxShadow: 'none', borderRadius: 2, width: '100%', minWidth: 600, background: '#fff' }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 600, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012', color: '#fff', fontWeight: 'bold',
                          fontSize: { xs: '0.75rem', sm: '0.85rem' }, whiteSpace: 'nowrap',
                          padding: { xs: '6px', sm: '8px' }, borderBottom: '1px solid #e0e0e0',
                          borderRight: '2px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {active.loading ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : active.error || payload?.error ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4, color: '#d32f2f' }}>
                        {active.error?.message || payload?.error}
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} align="center" sx={{ fontSize: '1rem', color: '#888', py: 4 }}>
                        {kind === 'ais'
                          ? 'No certificates expiring in this window. Note the AIS-140 issue date is only known for vehicles certified through this tool or back-filled from the legacy OEM tool.'
                          : 'No Saarthi Pro subscriptions expiring in this window.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r, idx) => (
                      <TableRow key={`${r.vin}-${idx}`} hover sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                        <TableCell align="center" sx={cellStyle}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{r.customerName || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{r.customerMobile || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{r.vin || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{r.imei || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>{r.vehicleModel || 'N/A'}</TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            color: kind === 'ais' ? severityColor(r.severity) : undefined,
                            fontWeight: kind === 'ais' ? 600 : 400,
                          }}
                        >
                          {fmtEpoch(r.claExpiresOn)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            ...cellStyle,
                            color: kind === 'saarthi_pro' ? severityColor(r.severity) : undefined,
                            fontWeight: kind === 'saarthi_pro' ? 600 : 400,
                          }}
                        >
                          {fmtEpoch(r.saarthiProExpiryDate)}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{fmtDays(r.daysToExpiry)}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Chip
                            size="small"
                            clickable={false}
                            label={severityLabel(r.severity)}
                            sx={{
                              height: 20, fontSize: '0.7rem', fontWeight: 600,
                              color: severityColor(r.severity),
                              backgroundColor: `${severityColor(r.severity)}1A`,
                              border: `1px solid ${severityColor(r.severity)}`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <RechargeTypeTag value={r.rechargeType} />
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>{r.dealerName || 'N/A'}</TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {r.lastCallAt ? (
                            <Tooltip
                              title={[
                                r.lastCallBy && `Logged by ${r.lastCallBy}`,
                                r.lastCallAttendedBy && `Attended by ${r.lastCallAttendedBy}`,
                              ].filter(Boolean).join(' · ')}
                              placement="top"
                              arrow
                            >
                              <span>{fmtEpoch(r.lastCallAt)}</span>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          {r.lastCallReason ? (
                            <Tooltip title={r.lastCallReason} placement="top" arrow>
                              <Chip
                                size="small"
                                label={r.lastCallReason}
                                sx={{
                                  height: 20, fontSize: '0.7rem', maxWidth: 170,
                                  color: '#ed6c02', backgroundColor: '#ed6c021A',
                                  border: '1px solid #ed6c02',
                                }}
                              />
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell align="center" sx={cellStyle}>
                          <Tooltip
                            title={canCreate('CERTIFICATE-EXPIRY') ? '' : 'You need create access on CERTIFICATE-EXPIRY to log a call.'}
                            placement="top"
                            arrow
                          >
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                                onClick={() => setSelected(r)}
                                disabled={!canCreate('CERTIFICATE-EXPIRY')}
                                sx={{
                                  backgroundColor: '#58B80F1A', color: '#58B80F',
                                  border: '1px solid #58B80F', borderRadius: '20px',
                                  height: 24, fontSize: 12, textTransform: 'none', boxShadow: 'none',
                                  '&:hover': { backgroundColor: '#58B80F33', boxShadow: 'none' },
                                }}
                              >
                                Call
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'sticky', bottom: 0, zIndex: 20, background: '#fff',
            borderTop: '1px solid #e0e0e0', pt: 2, mt: 'auto', overflowX: 'auto',
          }}
        >
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Rows per page:"
          />
        </Box>
      </Card>

      <CallLogDialog
        open={!!selected}
        vehicle={selected}
        kind={kind}
        busy={callBusy}
        onClose={() => setSelected(null)}
        onSubmit={handleLogCall}
      />
    </Box>
  );
};

export default CertificateExpiry;
